import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Partner,
  ReceiptFilter,
  ReceiptsResponse,
  ReceiptsTableData,
  ReceiptsUiFilter,
  ReceiptTableEntry,
  ReceiptType,
} from '@pay/receipts/data/receipts.model';
import { ReceiptDownloadStateService } from '@pay/receipts/services/receipt-download-state.service';
import { ReceiptMapperService } from '@pay/receipts/services/receipt-mapper.service';
import { ReceiptSheetService } from '@pay/receipts/services/receipt-sheet.service';
import { ReceiptsService } from '@pay/receipts/services/receipts.service';
import { NotificationLevel } from '@shared/services/notification/notification-level.model';
import { SnackbarService } from '@shared/services/notification/snackbar.service';
import { TopNotificationService } from '@shared/services/notification/top-notification.service';
import { StringUtil } from '@shared/util/string-util/string-util';
import { ButtonOption } from '@widgets/dropdown-button/dropdown-button.component';
import { DropdownItem } from '@widgets/dropdown/dropdown-type';
import { FilterContainerConfiguration } from '@widgets/filter/specific/filter-container.component';
import { FilterBackendService } from '@widgets/filter/specific/services/filter-backend.service';
import { FilterUtilService } from '@widgets/filter/specific/services/filter-util.service';
import { TableSortSettings } from '@widgets/innogy-data-table/tableDefinition/table-settings.interface';
import { ITableConfig } from '@widgets/innogy-data-table/tableDefinition/tableConfig.interface';
import { saveAs } from 'file-saver';
import { debounceTime, filter, Subject, switchMap, takeUntil } from 'rxjs';
import { ReceiptsTableFilterConfig } from '../../config/filter.config';
import { ReceiptsTableConfig } from '../../config/table.config';
import { DataTableRow } from '@widgets/innogy-data-table/data/data-table-row.data';

export const merchantType = 'merchant';
export const evDriverType = 'evDriver';
export const defaultSortColumn = 'startTimestamp';
export const defaultSortDirection = 'DESC';
export const defaultPageSize = 50;

@Component({
  selector: 'eop-receipt-overview',
  templateUrl: './receipt-overview.component.html',
  styleUrl: './receipt-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReceiptOverviewComponent implements OnInit, OnChanges, OnDestroy {
  readonly DownloadExcel = 'DOWNLOAD_EXCEL';
  readonly DownloadZip = 'DOWNLOAD_ZIP';
  readonly exportTimeLimitInDays = 31;
  readonly exportLimitInReceipts = 1000;

  @Input({ required: true }) receiptType: ReceiptType;
  @Input() selectedPartnerItem: DropdownItem<Partner>;
  @Input() hideFirstBorder = false;

  filter: ReceiptFilter = { page: 0 };
  searchTerms$ = new Subject<void>();
  searchInProgress = false;
  totalItems = 0;
  result: ReceiptsTableData = {
    paging: {
      itemsPerPage: defaultPageSize,
    },
    data: [],
  };

  tableConfig: ITableConfig;
  filterComponents: FilterContainerConfiguration[] = [];
  excelDownloadInProgress = false;
  zipDownloadInProgress = false;
  zipGenerationInProgress = false;
  exportLimitExceeded = false;
  exportTimeLimitExceeded = false;

  createOptions: ButtonOption[] = [];

  private readonly unsubscribe$ = new Subject<void>();

  private readonly receiptsService = inject(ReceiptsService);
  private readonly translate = inject(TranslateService);
  private readonly topNotificationService = inject(TopNotificationService);
  private readonly filterBackendService = inject(FilterBackendService);
  private readonly filterUtilService = inject(FilterUtilService);
  private readonly receiptSheetService = inject(ReceiptSheetService);
  private readonly receiptMapperService = inject(ReceiptMapperService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly downloadStateService = inject(ReceiptDownloadStateService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.createOptions = [
      {
        label: this.translate.instant('RECEIPTS.EXCEL_DOWNLOAD'),
        value: this.DownloadExcel,
        description: this.translate.instant('RECEIPTS.EXCEL_DOWNLOAD_DESCRIPTION'),
      },
      {
        label: this.translate.instant('RECEIPTS.ZIP_EXPORT'),
        value: this.DownloadZip,
        description: this.translate.instant('RECEIPTS.ZIP_EXPORT_DESCRIPTION'),
      },
    ];
    this.filterComponents = ReceiptsTableFilterConfig.getFilterConfig(
      this.translate,
      this.receiptsService,
      this.filterBackendService,
      this.filterUtilService,
      this.receiptType
    );
    this.tableConfig = ReceiptsTableConfig.getConfig(this.translate, this.receiptType);
    this.initSearch();
    this.monitorDownloadState();
    this.searchTerms$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPartnerItem'] && !changes['selectedPartnerItem'].firstChange) {
      if (this.selectedPartnerItem) {
        this.triggerSearch();
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onFilter(event: ReceiptsUiFilter): void {
    this.filter = {
      ...this.filter,
      page: 0,
      cardNumberSuffix: event.cardNumberSuffix,
      cardType: event.cardType,
      chargePortName: event.chargePortName,
      sessionStartFrom: event.sessionStartFrom,
      sessionStartTo: event.sessionStartTo,
      terminalId: event.terminalId,
      transactionId: event.transactionId,
      email: event.email,
    };
    this.resetResult();
    this.searchTerms$.next();
    this.exportTimeLimitExceeded = this.isExportTimeLimitExceeded(
      this.filter.sessionStartFrom,
      this.filter.sessionStartTo
    );
  }

  onDownload(row: DataTableRow): void {
    this.receiptsService
      .downloadReceipt(this.selectedPartnerItem.value.id, this.receiptType, row.data.downloadId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: response => {
          saveAs(
            StringUtil.stringToBlob(response.fileContent, 'application/pdf'),
            response.filename
          );
        },
        error: (error: HttpErrorResponse) => {
          this.topNotificationService.showErrorInTopNotification(error);
        },
      });
  }

  requireNewData(event: { page: number }): void {
    this.filter = {
      ...this.filter,
      page: event.page,
    };
    this.searchInProgress = true;
    this.cdr.detectChanges();
    this.searchTerms$.next();
  }

  orderByColumn(event: TableSortSettings): void {
    this.resetResult();
    this.filter = {
      ...this.filter,
      sortColumn: event.column,
      sortOrder: event.direction,
    };
    this.searchTerms$.next();
  }

  onDownloadOption(optionValue: string): void {
    if (optionValue === this.DownloadExcel) {
      this.onExportSheet();
    } else if (optionValue === this.DownloadZip) {
      this.onExportZip();
    }
  }

  triggerSearch(): void {
    this.resetResult();
    this.searchTerms$.next();
  }

  resetResult(): void {
    this.searchInProgress = true;
    this.result = {
      paging: {
        itemsPerPage: defaultPageSize,
      },
      data: [],
    };
    this.result = Object.assign({}, this.result);
    this.cdr.detectChanges();
  }

  private onExportSheet(): void {
    this.excelDownloadInProgress = true;
    this.receiptSheetService
      .exportToSheet(
        this.selectedPartnerItem.value.id,
        this.filter,
        this.tableConfig.columns,
        this.receiptType
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.excelDownloadInProgress = false;
          this.cdr.detectChanges();
        },
        error: error => {
          this.topNotificationService.showErrorInTopNotification(error);
          this.excelDownloadInProgress = false;
          this.cdr.detectChanges();
        },
      });
  }

  private onExportZip(): void {
    this.snackbarService.closeSnackbar();
    this.zipDownloadInProgress = true;

    this.receiptsService
      .exportReceiptsAsZip(this.selectedPartnerItem.value.id, this.receiptType, this.filter)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.downloadStateService.setDownload({
            partnerId: this.selectedPartnerItem.value.id,
            receiptType: this.receiptType,
            downloadId: null,
            fileName: null,
            progress: 0,
            startTime: Date.now(),
          });

          this.zipDownloadInProgress = false;
          this.zipGenerationInProgress = true;
          this.cdr.detectChanges();
        },
        error: (error: HttpErrorResponse) => {
          this.zipDownloadInProgress = false;
          if (error.status === 409) {
            this.snackbarService.openSnackbar(
              this.translate.instant('RECEIPTS.DOWNLOAD_CONFLICT'),
              NotificationLevel.WARNING
            );
          } else {
            this.topNotificationService.showErrorInTopNotification(error);
          }
          this.cdr.detectChanges();
        },
      });
  }

  private monitorDownloadState(): void {
    this.downloadStateService
      .getActiveDownload()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(download => {
        this.zipGenerationInProgress = download != null;
        this.cdr.detectChanges();
      });
  }

  private initSearch(): void {
    const DEBOUNCE_TIME_BEFORE_SEARCH = 700;

    this.searchTerms$
      .pipe(
        debounceTime(DEBOUNCE_TIME_BEFORE_SEARCH),
        filter(() => !!this.selectedPartnerItem),
        switchMap(() =>
          this.receiptsService.getReceipts(
            this.selectedPartnerItem.value.id,
            this.filter,
            this.receiptType
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (response: ReceiptsResponse) => {
          this.searchInProgress = false;
          this.totalItems = response.paging.totalItems;
          this.exportLimitExceeded = this.totalItems > this.exportLimitInReceipts;
          const tempReceipts: ReceiptTableEntry[] = this.receiptMapperService.mapToTableData(
            response.receipts
          );
          this.result.data = this.result.data.concat(tempReceipts);
          this.result.paging = response.paging;
          this.result = Object.assign({}, this.result);
          this.cdr.detectChanges();
        },
        error: error => {
          this.searchInProgress = false;
          this.initSearch();
          this.topNotificationService.showErrorInTopNotification(error);
        },
      });
  }

  private isExportTimeLimitExceeded(start: string, end: string): boolean {
    return start && end
      ? (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24) >
          this.exportTimeLimitInDays + 1
      : false;
  }
}
